/**
 * SimpleActionRail — right-side action rail in simple mode.
 *
 * Order: Assistant / Importer / Score ATS / Offres / Adapter / Traduire / Lettre / Mise en page / Exporter / Candidatures
 */

import { memo, useCallback, useRef, useState } from 'react';
import { WandSparkles, Mail, Globe, ChartNoAxesCombined, Save, Zap, CheckCircle2, XCircle, Briefcase, LayoutDashboard, Palette, FileInput } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { InfoTooltip } from '../ui/InfoTooltip';
import { useI18n } from '@/contexts/I18nContext';
import { useModal } from '@/contexts/ModalContext';
import { useNotif, type NotifAction } from '@/contexts/NotifContext';

export type SimpleModalType = 'adapt' | 'letter' | 'translate' | 'score' | 'export' | 'offers' | null;

interface SimpleActionRailProps {
  isMiseEnPageOpen: boolean;
  onMiseEnPageToggle: () => void;
}

export const SimpleActionRail = memo(function SimpleActionRail({ isMiseEnPageOpen, onMiseEnPageToggle }: SimpleActionRailProps) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { activeModal, setActiveModal } = useModal();
  const { pending, clear, registerTarget } = useNotif();
  const [confirmWizard, setConfirmWizard] = useState(false);

  // Action ids that participate in notifications (subset of SimpleModalType).
  const NOTIF_IDS: NotifAction[] = ['score', 'adapt', 'translate', 'letter'];

  // Keep a ref callback factory so registerTarget gets called on mount/unmount
  // with the actual button DOM node — the overlay uses these to aim flights.
  const registerRef = useCallback(
    (action: NotifAction) => (el: HTMLElement | null) => registerTarget(action, el),
    [registerTarget],
  );
  const assistantRef = useRef<HTMLButtonElement | null>(null);
  void assistantRef;

  // Modal buttons (ordered as they appear between Assistant/Importer and Mise en page/Export/Dashboard)
  const MODAL_BUTTONS: Array<{
    id: Exclude<SimpleModalType, null>;
    icon: React.ElementType;
    label: string;
    color: string;
    tooltip?: string;
  }> = [
    { id: 'score',     icon: ChartNoAxesCombined, label: t('actionRail.scoreAts'), color: 'text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/20', tooltip: t('actionRail.tooltipScore') },
    { id: 'offers',    icon: Briefcase,            label: t('actionRail.offers'),   color: 'text-blue-300 bg-blue-500/10 hover:bg-blue-500/20 border-blue-500/20' },
    { id: 'adapt',     icon: WandSparkles,         label: t('actionRail.adapt'),    color: 'text-indigo-400 bg-indigo-500/10 hover:bg-indigo-500/20 border-indigo-500/20', tooltip: t('actionRail.tooltipAdapt') },
    { id: 'translate', icon: Globe,                label: t('actionRail.translate'),color: 'text-cyan-400 bg-cyan-500/10 hover:bg-cyan-500/20 border-cyan-500/20', tooltip: t('actionRail.tooltipTranslate') },
    { id: 'letter',    icon: Mail,                 label: t('actionRail.letter'),   color: 'text-violet-400 bg-violet-500/10 hover:bg-violet-500/20 border-violet-500/20', tooltip: t('actionRail.tooltipLetter') },
  ];

  return (
    <div className="h-full flex flex-col items-center py-6 px-2 gap-2 border-l border-white/10 bg-white/[0.01] overflow-y-auto">

      {/* 1. Assistant (wizard) — with confirmation */}
      {!confirmWizard ? (
        <button
          type="button"
          onClick={() => setConfirmWizard(true)}
          className="flex flex-col items-center gap-1.5 w-full py-3 px-2 rounded-xl border border-amber-500/20 text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 transition-all text-center"
        >
          <Zap className="size-5" />
          <span className="text-[11px] font-semibold leading-none">{t('actionRail.assistant')}</span>
        </button>
      ) : (
        <div className="w-full rounded-xl border border-amber-500/30 bg-amber-500/10 p-2 space-y-1.5 text-center">
          <p className="text-[9px] text-amber-300 font-medium leading-snug">{t('actionRail.wizardConfirm')}</p>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => navigate('/wizard')}
              className="flex-1 flex items-center justify-center gap-0.5 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 transition-colors"
            >
              <CheckCircle2 className="size-3" />
              <span className="text-[9px] font-bold">{t('actionRail.yes')}</span>
            </button>
            <button
              type="button"
              onClick={() => setConfirmWizard(false)}
              className="flex-1 flex items-center justify-center gap-0.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/50 transition-colors"
            >
              <XCircle className="size-3" />
              <span className="text-[9px] font-medium">{t('actionRail.no')}</span>
            </button>
          </div>
        </div>
      )}

      {/* 2. Importer */}
      <button
        type="button"
        onClick={() => navigate('/wizard?mode=import')}
        className="flex flex-col items-center gap-1.5 w-full py-3 px-2 rounded-xl border border-teal-500/20 text-teal-400 bg-teal-500/10 hover:bg-teal-500/20 transition-all text-center"
        title={t('actionRail.importTitle')}
      >
        <FileInput className="size-5" />
        <span className="text-[11px] font-semibold leading-none">{t('actionRail.import')}</span>
      </button>

      {/* 3–7. Score ATS / Offres / Adapter / Traduire / Lettre */}
      {MODAL_BUTTONS.map(({ id, icon: Icon, label, color, tooltip }) => {
        const isNotifAction = (NOTIF_IDS as string[]).includes(id);
        const hasNotif = isNotifAction && pending[id as NotifAction];
        return (
        <div key={id} className="relative w-full">
          <button
            type="button"
            ref={isNotifAction ? registerRef(id as NotifAction) : undefined}
            onClick={() => {
              if (isNotifAction) clear(id as NotifAction);
              setActiveModal(activeModal === id ? null : id);
            }}
            className={cn(
              'flex flex-col items-center gap-1.5 w-full py-3 px-2 rounded-xl border transition-all text-center',
              color,
              activeModal === id && 'ring-2 ring-white/20',
            )}
          >
            <Icon className="size-5" />
            <span className="text-[11px] font-semibold leading-none">{label}</span>
          </button>
          {hasNotif && (
            <span
              aria-label="Nouveau résultat disponible"
              className="absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center"
            >
              <span className="absolute inline-flex h-full w-full rounded-full bg-rose-500 opacity-75 animate-ping" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-rose-500 ring-2 ring-[#0a0a0a]" />
            </span>
          )}
          {tooltip && (
            <div className="absolute top-1 right-1">
              <InfoTooltip content={tooltip} placement="top" />
            </div>
          )}
        </div>
        );
      })}

      {/* 8. Mise en page toggle */}
      <button
        type="button"
        onClick={onMiseEnPageToggle}
        className={cn(
          'flex flex-col items-center gap-1.5 w-full py-3 px-2 rounded-xl border transition-all text-center',
          isMiseEnPageOpen
            ? 'border-purple-500/30 text-purple-300 bg-purple-500/20 ring-2 ring-white/20'
            : 'border-purple-500/20 text-purple-400 bg-purple-500/10 hover:bg-purple-500/20',
        )}
        title={t('actionRail.layoutTitle')}
      >
        <Palette className="size-5" />
        <span className="text-[11px] font-semibold leading-none">{t('actionRail.layout')}</span>
      </button>

      {/* 9. Exporter */}
      <div className="relative w-full">
        <button
          type="button"
          onClick={() => setActiveModal(activeModal === 'export' ? null : 'export')}
          className={cn(
            'flex flex-col items-center gap-1.5 w-full py-3 px-2 rounded-xl border transition-all text-center',
            'text-orange-400 bg-orange-500/10 hover:bg-orange-500/20 border-orange-500/20',
            activeModal === 'export' && 'ring-2 ring-white/20',
          )}
        >
          <Save className="size-5" />
          <span className="text-[11px] font-semibold leading-none">{t('actionRail.export')}</span>
        </button>
      </div>

      {/* 10. Candidatures */}
      <button
        type="button"
        onClick={() => navigate('/dashboard')}
        className="flex flex-col items-center gap-1.5 w-full py-3 px-2 rounded-xl border border-rose-500/20 text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 transition-all text-center"
        title={t('actionRail.applicationsTitle')}
      >
        <LayoutDashboard className="size-5" />
        <span className="text-[11px] font-semibold leading-none">{t('actionRail.applications')}</span>
      </button>
    </div>
  );
});
